import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import bbox from "@turf/bbox";
import booleanContains from "@turf/boolean-contains";
import booleanIntersects from "@turf/boolean-intersects";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { uniqBy } from "lodash";
import { LngLatLike } from "mapbox-gl";
import { passing_draw_polygon as passingDrawMode } from "mapbox-gl-draw-passing-mode";

import { isLineString, isPoint } from "fond/types/geojson";

import { NBN_SOURCE, NBN_SOURCE_LAYER, NBN_SOURCE_LAYER_FILTER } from "./constants";

/**
 * This is a custom draw mode that allows for the user to select existing draw features on the map
 * or to select features from the NBN_SOURCE_LAYER layer.
 *
 * Selection of features is by drawing a polygon on the map.
 */
const nbnPassingDrawMode: DrawCustomMode = {
  ...passingDrawMode,

  onStop: function (state) {
    const { polygon } = state;
    const { map } = this;

    const opts = this._ctx.options;

    if (polygon.coordinates.flat().length !== 0) {
      // Get all features within the bounding box of the drawn feature
      const polygonBounds = bbox(polygon);
      const southWest = [polygonBounds[0], polygonBounds[1]] as LngLatLike;
      const northEast = [polygonBounds[2], polygonBounds[3]] as LngLatLike;
      const northEastPointPixel = map.project(northEast);
      const southWestPointPixel = map.project(southWest);

      const vectorFeatures = uniqBy(
        map
          ?.queryRenderedFeatures([southWestPointPixel, northEastPointPixel], {
            layers: [...(opts?.styles?.map(({ id }) => id) || []), ...(opts?.selectableLayerIds || [])],
            filter: NBN_SOURCE_LAYER_FILTER,
          })
          .filter((f) => {
            if (f.properties?.id !== undefined) {
              // check if feature is within or intersects the polygon
              if (isPoint(f)) {
                return booleanPointInPolygon(f, polygon);
              } else if (isLineString(f)) {
                return booleanContains(polygon, f) || booleanIntersects(f, polygon);
              }
            }
            return false;
          }),
        (f) => f.properties?.id || f.id
      );

      // Need to create the drawFeatures from the vector features
      // and add them to the drawControl
      vectorFeatures.forEach((feature) => {
        this.addDrawFeature(feature);
      });
    }
    passingDrawMode.onStop?.call(this, state);
  },

  /**
   * Adds a draw feature to the drawControl and sets the feature state to isEditing for the underlying
   * vector feature (which styles use to hide the feature behind the draw feature)
   */
  addDrawFeature(this: MapboxDraw.DrawCustomModeThis, feature: mapboxgl.MapboxGeoJSONFeature) {
    const drawFeature = this.newFeature(feature);
    const featureId = feature.properties?.id;
    this.map?.setFeatureState({ id: featureId, source: NBN_SOURCE, sourceLayer: NBN_SOURCE_LAYER }, { isEditing: true });
    this.addFeature(drawFeature);

    return drawFeature;
  },
};

export default nbnPassingDrawMode;
